// simulator/idb.js
import { normalizeAndSort } from './utils.js';

function idbReq(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
    });
}

// Renvoie false si la DB n'existe pas ou si l'API n'est pas dispo
async function dbExists(name) {
    try {
        if (!('databases' in indexedDB)) return false; // mieux: considérer "absente" => on ne tente pas d'ouvrir
        const dbs = await indexedDB.databases();
        return dbs.some((d) => d.name === name);
    } catch {
        return false;
    }
}

export function createIdbReader({ DB_NAME, STORE, UPDATED_FIELD, UPDATED_INDEX, withDb, OFFLINE_MODE_ENABLED }) {
    let nativeDb = null;

    async function openNativeDb() {
        // Ne plus throw si absente: on renvoie null pour signaler "pas de DB"
        const exists = await dbExists(DB_NAME);
        if (!exists) return null;

        return await new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME);
            req.onupgradeneeded = () => {
                // Le viewer est read-only : si upgrade, on abandonne et on renvoie null
                try { req.transaction.abort(); } catch { }
                resolve(null);
            };
            req.onsuccess = () => {
                const database = req.result;
                if (!database.objectStoreNames.contains(STORE)) {
                    // Pas de store attendu : on ferme et on considère "pas de données"
                    try { database.close(); } catch { }
                    resolve(null);
                    return;
                }
                database.onversionchange = () => { try { database.close(); } catch { } };
                resolve(database);
            };
            req.onerror = () => resolve(null); // fallback silencieux
        });
    }

    async function fetchLatest10_native() {
        // Si pas de DB native -> aucune donnée locale à afficher
        if (!nativeDb) return [];
        const tx = nativeDb.transaction(STORE, 'readonly');
        const store = tx.objectStore(STORE);

        let hasIndex = false;
        try { hasIndex = store.indexNames.contains(UPDATED_INDEX); } catch { hasIndex = false; }

        if (hasIndex) {
            return await new Promise((resolve, reject) => {
                const out = [];
                const req = store.index(UPDATED_INDEX).openCursor(null, 'prev');
                req.onerror = () => reject(req.error);
                req.onsuccess = (ev) => {
                    const cur = ev.target.result;
                    if (cur && out.length < 10) {
                        out.push(cur.value);
                        cur.continue();
                    } else {
                        resolve(out);
                    }
                };
            });
        }

        const all = await idbReq(store.getAll());
        return normalizeAndSort(all, UPDATED_FIELD).slice(0, 10);
    }

    async function fetchLatest10_withHelper() {
        try {
            const all = await withDb((db) => db.getAll(STORE));
            if (!Array.isArray(all)) return [];
            return normalizeAndSort(all, UPDATED_FIELD).slice(0, 10);
        } catch {
            return [];
        }
    }

    async function fetchLatest10() {
        // 1) Si l'app est en mode offline et que withDb existe, on tente le helper
        if (OFFLINE_MODE_ENABLED && typeof withDb === 'function') {
            const items = await fetchLatest10_withHelper();
            if (items.length) return items;
            // sinon on essaie la voie native (si DB existe)
        }

        // 2) Voie native: ouvrir la DB si pas déjà fait
        if (!nativeDb) {
            nativeDb = await openNativeDb(); // peut renvoyer null => pas de DB
        }

        // 3) Si toujours pas de DB => pas de données locales (aucune exception)
        if (!nativeDb) return [];

        return await fetchLatest10_native();
    }

    return {
        openNativeDb,
        fetchLatest10,
    };
}
